import { useState } from 'react';
import { Card, CardContent } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input, FormField } from '../../../components/ui/FormField';
import { FormSelect } from '../../../components/ui/FormSelect';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/Dialog';
import { Skeleton } from '../../../components/ui/Skeleton';
import { EmptyState } from '../../../components/ui/EmptyState';
import { useToast } from '../../../components/ui/Toast';

export interface SubEntityFieldConfig {
  name: string;
  label: string;
  widget?: 'text' | 'number' | 'date' | 'select' | 'checkbox';
  required?: boolean;
  options?: { value: string; label: string }[];
}

export interface SubEntityColumnConfig<T> {
  key: keyof T;
  label: string;
  render?: (row: T) => React.ReactNode;
}

interface CustomerSubEntityTabProps<T extends { id: string }, TCreate extends Record<string, unknown>> {
  title: string;
  addButtonLabel: string;
  emptyLabel: string;
  fields: SubEntityFieldConfig[];
  columns: SubEntityColumnConfig<T>[];
  hooks: {
    useList: (customerId: string) => { data?: T[]; isLoading: boolean; isError: boolean; refetch: () => void };
    useCreate: (customerId: string) => { mutateAsync: (body: TCreate) => Promise<unknown>; isPending: boolean };
    useDelete: (customerId: string) => { mutateAsync: (id: string) => Promise<unknown>; isPending: boolean };
  };
  customerId: string;
}

/**
 * Shared list+add-modal+delete UI for the customer child entities (banks, nominees, education,
 * occupation, introducers) -- all 5 are structurally identical (a flat list of records scoped to
 * one customer, created via a small form, removed via soft-delete), differing only in which
 * fields they collect and display. Modeled on SchemeDocumentsTab.tsx's list+modal+delete pattern.
 */
export function CustomerSubEntityTab<T extends { id: string }, TCreate extends Record<string, unknown>>({
  title,
  addButtonLabel,
  emptyLabel,
  fields,
  columns,
  hooks,
  customerId,
}: CustomerSubEntityTabProps<T, TCreate>) {
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [formValues, setFormValues] = useState<Record<string, unknown>>({});
  const { toast } = useToast();

  const { data, isLoading, isError, refetch } = hooks.useList(customerId);
  const createMutation = hooks.useCreate(customerId);
  const deleteMutation = hooks.useDelete(customerId);

  const rows = data ?? [];

  const resetForm = () => setFormValues({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload: Record<string, unknown> = {};
      for (const field of fields) {
        const value = formValues[field.name];
        if (value === undefined || value === '') continue;
        payload[field.name] = field.widget === 'number' ? parseInt(String(value), 10) : field.widget === 'checkbox' ? Boolean(value) : value;
      }
      // payload is necessarily built at runtime from the caller-supplied `fields` config (its
      // shape can't be statically known here) -- the cast asserts what the caller already promised
      // by choosing field names that match TCreate's keys, not a bypass of a real type mismatch.
      await createMutation.mutateAsync(payload as TCreate);
      toast({ type: 'success', title: `${title.replace(/s$/, '')} added` });
      setIsAddOpen(false);
      resetForm();
    // allow-silent-catch: error is surfaced to the user via toast(), not console/logger
    } catch (err: unknown) {
      toast({ type: 'error', title: 'Save failed', description: err instanceof Error ? err.message : undefined });
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm(`Remove this ${title.toLowerCase().replace(/s$/, '')} record?`)) return;
    try {
      await deleteMutation.mutateAsync(id);
      toast({ type: 'success', title: 'Removed' });
    // allow-silent-catch: error is surfaced to the user via toast(), not console/logger
    } catch (err: unknown) {
      toast({ type: 'error', title: 'Remove failed', description: err instanceof Error ? err.message : undefined });
    }
  };

  if (isLoading) return <Skeleton rows={4} />;
  if (isError) return (
    <Card className="border-danger-500">
      <CardContent className="p-6 text-center text-danger-text">
        Failed to load {title.toLowerCase()}
        <Button variant="secondary" size="sm" className="ml-3" onClick={() => refetch()}>Retry</Button>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-text-primary">{title}</h3>
        <Button onClick={() => setIsAddOpen(true)}>{addButtonLabel}</Button>
      </div>

      <Card className="border border-border shadow-sm overflow-hidden">
        <CardContent className="p-0">
          {rows.length === 0 ? (
            <EmptyState title={emptyLabel} description={`Click "${addButtonLabel}" to add one.`} icon={<span />} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-surface-2">
                  <tr>
                    {columns.map((col) => (
                      <th key={String(col.key)} className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">
                        {col.label}
                      </th>
                    ))}
                    <th className="px-4 py-3 text-right text-xs font-semibold text-text-secondary uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {rows.map((row) => (
                    <tr key={row.id} className="hover:bg-surface-1">
                      {columns.map((col) => (
                        <td key={String(col.key)} className="px-4 py-3 text-sm text-text-primary">
                          {col.render ? col.render(row) : String(row[col.key] ?? '—')}
                        </td>
                      ))}
                      <td className="px-4 py-3 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-danger-text"
                          onClick={() => handleDelete(row.id)}
                          disabled={deleteMutation.isPending}
                        >
                          Remove
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{addButtonLabel}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {fields.map((field) => (
                <div key={field.name} className={field.widget === 'checkbox' ? 'flex items-center gap-2' : ''}>
                  {field.widget === 'select' ? (
                    <FormSelect
                      label={field.label}
                      required={field.required}
                      // allow-raw-sql: "Select ${...}" is a UI placeholder label, not SQL — heuristic false positive
                      options={[{ value: '', label: `Select ${field.label.toLowerCase()}...` }, ...(field.options ?? [])]}
                      value={(formValues[field.name] as string) ?? ''}
                      onChange={(e) => setFormValues((prev) => ({ ...prev, [field.name]: e.target.value }))}
                    />
                  ) : field.widget === 'checkbox' ? (
                    <>
                      <Input
                        type="checkbox"
                        id={field.name}
                        checked={Boolean(formValues[field.name])}
                        onChange={(e) => setFormValues((prev) => ({ ...prev, [field.name]: e.target.checked }))}
                        className="accent-accent-500"
                      />
                      <label htmlFor={field.name} className="text-sm text-text-secondary">{field.label}</label>
                    </>
                  ) : (
                    <FormField label={field.label} required={field.required}>
                      <Input
                        type={field.widget ?? 'text'}
                        value={(formValues[field.name] as string) ?? ''}
                        onChange={(e) => setFormValues((prev) => ({ ...prev, [field.name]: e.target.value }))}
                        required={field.required}
                      />
                    </FormField>
                  )}
                </div>
              ))}
            </div>
            <DialogFooter className="mt-6">
              <Button type="button" variant="secondary" onClick={() => setIsAddOpen(false)}>Cancel</Button>
              <Button type="submit" loading={createMutation.isPending}>Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
